/**
 * The camera, pointed at the setup code on the office screen (DECISIONS D-019).
 *
 * ── Why the app has to do its own scanning ──────────────────────────────────
 * D-013 assumed the phone's own camera app would read the QR and hand the URL
 * to the browser. On iOS that browser is SAFARI, and Safari's storage container
 * is not the installed app's — so the token landed in the wrong place and the
 * app the surveyor actually works in stayed unlinked. The installed app has to
 * open a camera of its own, inside its own container, and this is it.
 *
 * ── Nothing leaves the device (D-009) ───────────────────────────────────────
 * `jsqr` is a bundled dependency and the decode runs on a canvas in this page.
 * There is no upload, no web service and no CDN — which matters twice over
 * here: the app has to work in a basement, and the thing being decoded is a
 * live onboarding secret that must not be handed to a third party to read.
 *
 * ── The failures are told apart, because they need different answers ────────
 * "This phone will not give the app a camera" and "there is no camera at all"
 * and "you said no to the permission" are three different rooms to be stuck in,
 * and the typed code rescues all three — which is why every failure here ends
 * by pointing at it rather than at a Try Again the surveyor has already tried.
 *
 * ── Decoded once, and only once ─────────────────────────────────────────────
 * A steady hand on a bright screen decodes the same symbol on twenty
 * consecutive frames. Firing twenty redemptions of a single-use code would
 * refuse nineteen of them and, worse, the FIRST refusal is what the surveyor
 * would see if the ordering went badly. `done` latches on the first decode, the
 * loop stops, and the camera is released before the caller is told.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import jsQR from 'jsqr';

/** The longest edge the frame is scaled to before decoding. */
const DECODE_EDGE = 640;

export type ScannerProblem =
  /** No `getUserMedia` at all — an old browser, or an insecure origin. */
  | 'unsupported'
  /** The permission was refused, or the OS has cameras switched off. */
  | 'denied'
  /** There is a camera API but no camera behind it. */
  | 'no_camera'
  /** It opened and then stopped — a device in use, a tab switch that killed it. */
  | 'failed';

export interface QrScannerProps {
  /** The decoded text, once. Never called twice for one mounted scanner. */
  onDecoded: (text: string) => void;
  /** The surveyor pressed the way out. */
  onCancel: () => void;
  /** What went wrong, so the panel above can offer the typed code instead. */
  onProblem?: (problem: ScannerProblem) => void;
}

export function QrScanner({ onDecoded, onCancel, onProblem }: QrScannerProps): ReactNode {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [problem, setProblem] = useState<ScannerProblem | null>(null);
  /**
   * Held in a ref, not in state. The decode loop reads it on every frame and a
   * state value captured in the loop's closure would be the value from the
   * frame the loop started on — which is exactly the read that lets a second
   * decode through.
   */
  const done = useRef(false);

  // Kept in refs so the effect below can stay dependency-free: a re-render that
  // produced a new `onDecoded` identity would otherwise tear the camera down
  // and open it again, mid-scan, on every keystroke elsewhere on the screen.
  const decoded = useRef(onDecoded);
  const reported = useRef(onProblem);
  decoded.current = onDecoded;
  reported.current = onProblem;

  const fail = useCallback((kind: ScannerProblem) => {
    if (done.current) return;
    done.current = true;
    setProblem(kind);
    reported.current?.(kind);
  }, []);

  useEffect(() => {
    done.current = false;
    let stream: MediaStream | null = null;
    let frame = 0;
    let cancelled = false;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    const stop = (): void => {
      if (frame !== 0) cancelAnimationFrame(frame);
      frame = 0;
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = null;
    };

    const tick = (): void => {
      if (cancelled || done.current) return;
      const video = videoRef.current;

      if (video && context && video.readyState >= 2 && video.videoWidth > 0) {
        // Scaled down before decoding. A full 4K frame costs more per frame
        // than the phone can spend and finds nothing a 640px one would miss —
        // the office screen fills most of the viewfinder by the time anyone is
        // close enough to read it.
        const scale = Math.min(1, DECODE_EDGE / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        // `attemptBoth`: an office monitor in a dark room is routinely a light
        // symbol on a dark field once the camera has finished exposing for it.
        const found = jsQR(image.data, image.width, image.height, {
          inversionAttempts: 'attemptBoth',
        });

        if (found?.data) {
          done.current = true;
          stop();
          decoded.current(found.data);
          return;
        }
      }

      frame = requestAnimationFrame(tick);
    };

    const media = navigator.mediaDevices as MediaDevices | undefined;
    if (!media?.getUserMedia) {
      fail('unsupported');
      return () => {
        cancelled = true;
        stop();
      };
    }

    void media
      // `ideal`, not `exact`: a phone with only a front camera should still get
      // a camera rather than an OverconstrainedError, because a front camera
      // pointed at a screen in a mirror is still better than no scanner at all.
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((opened) => {
        if (cancelled) {
          for (const track of opened.getTracks()) track.stop();
          return;
        }
        stream = opened;
        const video = videoRef.current;
        if (!video) {
          stop();
          fail('failed');
          return;
        }
        video.srcObject = opened;
        // iOS refuses to start an inline video that is not muted, and refuses
        // to autoplay at all outside a user gesture — this component is only
        // ever mounted by a button press, so the gesture is already spent on it.
        void video.play().catch(() => fail('failed'));
        frame = requestAnimationFrame(tick);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        const name = (cause as { name?: string } | null)?.name ?? '';
        fail(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'denied'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'no_camera'
              : 'failed',
        );
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [fail]);

  if (problem !== null) {
    return (
      <div className="card" role="status" aria-live="polite">
        <p className="q" data-legibility="question">
          {problemHeadline(problem)}
        </p>
        <p className="ruleline" data-legibility="helper">
          {problemDetail(problem)}
        </p>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          Type the code instead
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Above the viewfinder, because it is the instruction for the
          viewfinder (D-011 item 6). */}
      <p className="leadin" data-legibility="helper">
        Hold the phone about a foot from the office screen, with the square code
        filling the box below.
      </p>

      <video
        ref={videoRef}
        className="scanner__view"
        // All three are required for an inline camera preview on iOS. Without
        // `playsInline` the phone opens the video full screen and the scan box
        // the surveyor was just told about is gone.
        playsInline
        muted
        autoPlay
        aria-label="Camera, looking for the setup code"
      />

      <button type="button" className="btn btn--ghost" onClick={onCancel}>
        Type the code instead
      </button>
    </div>
  );
}

/* ─────────────────── what each failure means, in English ─────────────────── */

function problemHeadline(problem: ScannerProblem): string {
  switch (problem) {
    case 'denied':
      return 'The camera is switched off for this app.';
    case 'no_camera':
      return 'This phone has no camera the app can use.';
    case 'unsupported':
      return 'This browser will not let the app use the camera.';
    case 'failed':
      return 'The camera would not start.';
  }
}

function problemDetail(problem: ScannerProblem): string {
  const fallback = 'You can type the setup code instead — it works exactly the same.';
  switch (problem) {
    case 'denied':
      return `Turn it on in Settings if you want to scan. ${fallback}`;
    default:
      return fallback;
  }
}
